/*
  For now, the FAQ items are hardcoded. In the future, we will add the ability to customize this page from lender to lender.
*/

import { useContext } from 'react';
import { Redirect } from 'react-router-dom';

import { LoanStatus } from '@willow/graphql-iso/src/portal';
import {
  FAQItem,
  FAQSection,
  getFAQData,
  NamedMemo,
  PORTAL_LOAN_PATHS,
  sanitizeAndDisplayHtml,
} from '@willow/shared-web';
import { Accordion } from '@willow/shared-web/bootstrap';

import { PortalSelectedLoan } from '../../App';
import { Metadata } from '../../components/metadata/Metadata';
import { PageHeading } from '../../components/page-heading/PageHeading';
import { FeatureFlagContext } from '../../contexts/featureFlagContext';

import './FAQ.scss';

interface Props {
  loan: PortalSelectedLoan;
}

export const FAQPage = NamedMemo<Props>('FAQPage', ({ loan }) => {
  const { losId, company } = loan;
  const { name: lenderName, supportEmail, supportPhone, customFAQ, adminSettings } = company;
  const { ff_autopaySettings: autoPayFeatureEnabled } = useContext(FeatureFlagContext);

  const faqData = getFAQData({
    lenderName,
    supportEmail,
    supportPhone,
    losId,
    companyId: company.id,
    autoPayFeatureEnabled,
    paymentProvider: adminSettings.repayEnabled ? 'repay' : 'dwolla',
  });

  const customFAQData: FAQSection[] = customFAQ.map((section) => {
    const items: FAQItem[] = section.items.map((item) => {
      return { question: item.question, answer: sanitizeAndDisplayHtml(item.answer) };
    });

    return { title: section.title, items };
  });

  if ([LoanStatus.Reo, LoanStatus.Liquidated].includes(loan.currentSnapshot.loanStatus)) {
    return <Redirect to={PORTAL_LOAN_PATHS.root({ losId: loan.losId, companyId: loan.company.id })} />;
  }

  return (
    <div className="page-faq">
      <Metadata metadata={{ title: 'Frequently Asked Questions' }} />

      <PageHeading title="Frequently asked questions" />

      <section className="faq-container">
        {faqData.concat(customFAQData).map((data, index) => {
          const { title, items } = data;
          return (
            <div key={`faq-section-${index}`} className="faq-section">
              {title && <h2 className="faq-section__title">{title}</h2>}
              {/* Default to first section + first item open */}
              <Accordion defaultActiveKey={index === 0 ? '0' : undefined}>
                {items.map((item, itemIndex) => {
                  if (!item) return null;
                  const { question, answer } = item;

                  return (
                    <Accordion.Item key={`faq-item-${index}-${itemIndex}`} eventKey={itemIndex.toString()}>
                      <Accordion.Header as="h3">{question}</Accordion.Header>
                      <Accordion.Body>{answer}</Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </div>
          );
        })}
      </section>
    </div>
  );
});
